define([
    '../Util/defined',
    '../System/ColorObj',
    '../Enum/EnumAlignmentMode',
    '../Enum/EnumColorMode',
    '../Enum/EnumRotateMode',
    '../Enum/EnumZoomMode',
], function (
    defined,
    ColorObj,
    EnumAlignmentMode,
    EnumColorMode,
    EnumRotateMode,
    EnumZoomMode,
) {
    'use strict'

    /**
     * 矢量图符号。
     * <br /><br />
     * <div align='center'>
     * <img src='images/VectorGraphSymbol.png' width='900' height='650' /><br />
     * 矢量图符号效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/VectorGraphSymbol.html|CooWasm VectorGraphSymbol demo}
     * @alias VectorGraphSymbol
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {EnumAlignmentMode} [props.alignMode=EnumAlignmentMode.CENTER_CENTER] 矢量图相对原点对齐方式。
     * @param {EnumRotateMode} [props.rotateMode=EnumRotateMode.TO_SCREEN] 矢量图朝向方式。
     * @param {EnumZoomMode} [props.zoomMode=EnumZoomMode.CAMERA] 矢量图缩放方式。
     * @param {Number} [props.zoomLimit=2000.0] 矢量图缩放上限值，单位：米。
     * @param {Number} [props.liftUp=0.0] 矢量图抬高高度，单位：米。
     * @param {Number} [props.minDistance=1.0] 最小可视距离。
     * @param {Number} [props.maxDistance=20000.0] 最大可视距离。
     * @param {Number} [props.scale=1] 矢量图缩放比例。
     * @param {String} props.info 矢量图信息，可以是图片路径，也可以是图片内容。
     * @param {EnumColorMode} [props.colorMode=EnumColorMode.ORIGINAL] 矢量图颜色渲染方式。
     * @param {ColorObj|String} [props.color] 矢量图颜色渲染值，可以是ColorObj对象或十六进制颜色字符串(如"#FF0000")。
     * @example
     * var icon = new CooWasm.VectorGraphSymbol({});
     */
    class VectorGraphSymbol {
        /**
         * @description 矢量图相对原点对齐方式。
         * @type {EnumAlignmentMode}
         */
        alignMode
        /**
         * @description 矢量图朝向方式。
         * @type {EnumRotateMode}
         */
        rotateMode
        /**
         * @description 矢量图缩放方式。
         * @type {EnumZoomMode}
         */
        zoomMode
        /**
         * @description 矢量图缩放上限值，单位：米。
         * @type {Number}
         */
        zoomLimit
        /**
         * @description 矢量图抬高高度，单位：米。
         * @type {Number}
         */
        liftUp
        /**
         * @description 最小可视距离。
         * @type {Number}
         */
        minDistance
        /**
         * @description 最大可视距离。
         * @type {Number}
         */
        maxDistance
        /**
         * @description 矢量图缩放比例
         * @type {Number}
         * @readonly
         */
        scale
        /**
         * @description 矢量图信息。
         * @type {String}
         */
        info
        /**
         * @description 矢量图颜色渲染方式。
         * @type {EnumColorMode}
         */
        colorMode
        /**
         * @description 矢量图颜色渲染值，可以是ColorObj对象或十六进制颜色字符串(如"#FF0000")。
         * @type {ColorObj|String}
         */
        color
        /**
         * @description 属性信息。
         * @private
         * @readonly
         */
        properties = ""
        /**
         * @description IconSymbol在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "VectorGraphSymbol"

        constructor(props) {
            if (!defined(props)) {
                props = {}
            }
            this.properties += this.toStr("alignMode", props.alignMode, "AlignmentMode")
            this.properties += this.toStr("rotateMode", props.rotateMode, "RotateMode")
            this.properties += this.toStr("zoomMode", props.zoomMode, "ZoomMode")
            this.properties += this.toStr("zoomLimit", props.zoomLimit, "ZoomMaxDistance")
            this.properties += this.toStr("liftUp", props.liftUp, "LiftUp")
            this.properties += this.toStr("minDistance", props.minDistance, "MinVisibleDistance")
            this.properties += this.toStr("maxDistance", props.maxDistance, "MaxVisibleDistance")
            this.properties += this.toStr("scale", props.scale, "Scale")
            this.properties += this.toStr("info", props.info, "VectorGraphicInfo")
            this.properties += this.toStr("colorMode", props.colorMode, "ColorMode")
            this.properties += this.toStr("color", props.color, "Color")
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        
        update(symbol) {
            let componentProps = ""
            if (!defined(symbol)) {
                return componentProps
            }
            if (defined(symbol.alignMode)) {
                this.alignMode = symbol.alignMode
                componentProps += "AlignmentMode:" + this.alignMode + "|"
            }
            if (defined(symbol.rotateMode)) {
                this.rotateMode = symbol.rotateMode
                componentProps += "RotateMode:" + this.rotateMode + "|"
            }
            if (defined(symbol.zoomMode)) {
                this.zoomMode = symbol.zoomMode
                componentProps += "ZoomMode:" + this.zoomMode + "|"
            }
            if (defined(symbol.zoomLimit)) {
                this.zoomLimit = symbol.zoomLimit
                componentProps += "ZoomMaxDistance:" + this.zoomLimit + "|"
            }
            if (defined(symbol.minDistance)) {
                this.minDistance = symbol.minDistance
                componentProps += "MinVisibleDistance:" + this.minDistance + "|"
            }
            if (defined(symbol.maxDistance)) {
                this.maxDistance = symbol.maxDistance
                componentProps += "MaxVisibleDistance:" + this.maxDistance + "|"
            }
            if (defined(symbol.liftUp)) {
                this.liftUp = symbol.liftUp
                componentProps += "LiftUp:" + this.liftUp + "|"
            }
            if (defined(symbol.scale)) {
                this.scale = symbol.scale
                componentProps += "Scale:" + this.scale + "|"
            }
            if (defined(symbol.info)) {
                this.info = symbol.info
                componentProps += "VectorGraphicInfo:" + this.info + "|"
            }
            if (defined(symbol.colorMode)) {
                this.colorMode = symbol.colorMode
                componentProps += "ColorMode:" + this.colorMode + "|"
            }
            if (defined(symbol.color)) {
                this.color = symbol.color
                if (typeof symbol.color.toStr === 'function') {
                    componentProps += "Color:" + symbol.color.toHexStr() + "|"
                } else {
                    componentProps += "Color:" + symbol.color + "|"
                }
            }
            return componentProps
        }
    }
    return VectorGraphSymbol
})